import {
  isCollectorPaymentType,
  isRedirectPaymentType,
  PaymentType,
} from "Components/Paypoints/paymentType";
import type { PaypointFormData } from "Components/Paypoints/schema";
import { FormSection } from "Components/Shared/FormSection";
import { CheckboxInput } from "Components/Shared/Inputs/Form/CheckboxInput";
import { Dropdown } from "Components/Shared/Inputs/Form/Dropdown";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { RadioButtons } from "Components/Shared/Inputs/Form/RadioButtons";
import { StyledInputLabel } from "Components/Shared/StyledComponents";
import { useCurrencyList } from "Hooks/Options/useCurrencyList";
import { PayPointType } from "Infrastructure/Api/Api";
import type { CodeListItem } from "Models/ICodeListDto";
import { Resources, useResource } from "Translations/Resources";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Typography } from "@mui/material";
import { type Control, useWatch } from "react-hook-form";

const RESOURCES = Resources.Paypoint.Form;

type Props = {
  control: Control<PaypointFormData>;
  selectedType: PayPointType;
  onPaymentTypeChange: (paymentType: string) => void;
};

export const PaypointPaymentSettingsSection = ({
  control,
  selectedType,
  onPaymentTypeChange,
}: Props) => {
  const { t } = useResource();
  const selectedPaymentType = useWatch({ control, name: "paymentType" });
  const redirectUrl = useWatch({ control, name: "redirectUrl" });
  // Show every currency the codelist knows, but disable the ones this environment hasn't
  // enabled — users see what's coming without being able to pick it (BE is the authority).
  const { codeListCurrencies: currencyCodeList } = useCurrencyList();

  const isGatewayType = selectedType === PayPointType.Gateway;
  const isRedirectType = isRedirectPaymentType(selectedPaymentType);
  const isTableService = selectedPaymentType === "NfcTableService";

  // Redirect modes only make sense on a physical tag.
  const paymentTypeCodeList: Array<CodeListItem> = PaymentType.filter(
    (code) => selectedType === PayPointType.NFC || !isRedirectPaymentType(code),
  ).map((code) => ({
    code,
    name: t(RESOURCES.PaymentType[code].label),
    description: t(RESOURCES.PaymentType[code].tooltip),
  }));

  return (
    <FormSection legend={t(RESOURCES.Legend.PaypointType)}>
      {isGatewayType && (
        <Typography
          component="span"
          display="flex"
          alignItems="flex-start"
          mb={3}
          gap={1}
        >
          <InfoOutlinedIcon sx={{ height: "1rem", width: "1rem", mt: 0.5 }} />
          {t(RESOURCES.GatewaySettingsHint)}
        </Typography>
      )}
      <StyledInputLabel>
        <Typography>{t(RESOURCES.PaymentTypeLabel)}</Typography>
      </StyledInputLabel>
      <Box mb={4}>
        <RadioButtons
          control={control}
          name="paymentType"
          codeList={paymentTypeCodeList}
          disabled={isGatewayType}
          onAfterChange={onPaymentTypeChange}
          isErrorHidden //No errors here, hides the empty space below component
        />
      </Box>
      {!isGatewayType && isCollectorPaymentType(selectedPaymentType) && (
        <Box display="flex" gap={2} alignItems="flex-start">
          {selectedPaymentType === "FixedPrice" && (
            <Box flexGrow={1} minWidth={0}>
              <FormInput
                control={control}
                name="fixedAmount"
                label={t(RESOURCES.FixedAmountLabel)}
                mask={Number}
                isNumber
                min={0}
                scale={2}
              />
            </Box>
          )}
          <Dropdown
            codeList={currencyCodeList}
            name="currencyCode"
            control={control}
            label={t(RESOURCES.CurrencyLabel)}
            formControlClass="medium"
          />
        </Box>
      )}
      {isRedirectType && (
        <FormInput
          control={control}
          name="redirectUrl"
          label={t(RESOURCES.RedirectUrlLabel)}
          placeholder="https://"
          disabled={isTableService}
          helperText={
            isTableService && !redirectUrl
              ? t(RESOURCES.TableServiceUrlMissing)
              : undefined
          }
        />
      )}
      <Box>
        <StyledInputLabel>
          <Typography>{t(RESOURCES.TipsLabel)}</Typography>
        </StyledInputLabel>
        <CheckboxInput
          control={control}
          name="tipsEnabled"
          label={t(RESOURCES.TipsCheckbox)}
          disabled={isGatewayType || isRedirectType}
        />
      </Box>
    </FormSection>
  );
};
